import { EEntityType, EOperateMode } from '@/editor/types';

export type OperateWallMode = EOperateMode.DrawWall | EOperateMode.DrawRectWall;

export type ModelEntityType = EEntityType.DoorWindow | EEntityType.Column;

export enum EParamType {
  /**
   * @description 数字输入框
   *
   * 可以搭配 options使用，如果有 options，则可以输入也可以下拉选择
   */
  NUMBER = 'number',
  /**
   * @description 文本输入框
   *
   * 可以搭配 options使用，如果有 options，则可以输入也可以下拉选择
   */
  TEXT = 'text',
  /**
   * @description 下拉选择框
   *
   * 只能下拉选择，不能输入
   */
  SELECT = 'select',
  /**
   * @description 滑块输入框
   *
   */
  SLIDER_INPUT = 'sliderInput',
  /**
   * @description 材质信息展示模块，用于家具/材质展示
   */
  MATERIAL_INFO = 'materialInfo',
  /**
   * @description 布局生成按钮
   */
  LAYOUT_GEN = 'layoutGen',
  /**
   * @description 多个数值展示框
   */
  MULTI_NUMBER = 'multiNumber'
}

export enum EParamsKeys {
  empty = 'empty',
  height = 'height',
  thickness = 'thickness',
  offGround = 'offGround',
  width = 'width',
  length = 'length',
  diameter = 'diameter',
  rotation = 'rotation',
  innerArea = 'innerArea',
  coreArea = 'coreArea',
  layerHeight = 'layerHeight',
  name = 'name',
  type = 'type',
  furnitureInfo = 'furnitureInfo',
  material = 'material',
  doorWindowInfo = 'doorWindowInfo',
  layoutGen = 'layoutGen',
  sagitta = 'sagitta',
  chord = 'chord',
  radius = 'radius'
}

export enum EParamLabel {
  LENGTH = 'draw.house.parameters.length',
  WALL_LENGTH = 'draw.house.parameters.walllength',
  WALL_THICKNESS = 'draw.house.parameters.wallthickness',
  WALL_HEIGHT = 'draw.house.parameters.wallheight',
  LAYER_HEIGHT = 'draw.house.parameters.innerheight',
  INNER_AREA = 'draw.house.parameters.innerarea',
  CORE_AREA = 'draw.house.parameters.wallarea',
  MODEL_WIDTH = 'draw.house.parameters.width',
  MODEL_HEIGHT = 'draw.house.parameters.height',
  MODEL_LENGTH = 'draw.house.parameters.depth',
  MODEL_OFFGROUND = 'draw.house.parameters.groundheight',
  SECTIONLENGTH = 'draw.house.parameters.sectionlength',
  SECTIONWIDTH = 'draw.house.parameters.sectionwidth',
  POSTHEIGHT = 'draw.house.parameters.postheight',
  SECTIONDIAMETER = 'draw.house.parameters.sectiondiameter',
  SECTIONROTATION = 'draw.house.parameters.sectionrotation',
  ROOM_NAME = 'draw.house.parameters.roomname',
  ROOM_TYPE = 'draw.house.parameters.roomtype',
  PLANE_ROTATE = 'draw.house.parameters.planerotate',
  SUBAREA_TYPE = 'draw.layout.tool.subareatype',
  SUBAREA_NAME = 'draw.layout.tool.subareaname',
  SEPARATE_WALL_TYPE = 'partition.class.partition',
  ROOM_LAYOUT_GEN = 'draw.layout.label',
  REGION_LAYOUT_GEN = 'draw.layout.tool.partTitle',
  SAGITTA = 'draw.house.parameters.sagitta',
  CHORD = 'draw.house.parameters.chord',
  ARC_WALL_LENGTH = 'draw.house.parameters.arc_length',
  RADIUS = 'draw.house.parameters.radius'
}

export enum EParamGroupLabel {
  DRAWWALL = 'draw.house.parameters.drawwall',
  ARC_WALL = 'draw.house.tool.arcwall',
  TYPE = 'draw.house.parameters.type',
  AREA = 'draw.house.parameters.area',
  FLOORS = 'draw.house.parameters.floors',
  WALL = 'draw.house.parameters.wall',
  ROOM = 'draw.house.parameters.room',
  SUBAREA = 'draw.layout.tool.subarea',
  WINDOW = 'draw.house.tool.window',
  FRENCH_WINDOW = 'draw.house.tool.windowceiling',
  SINGLEDOOR = 'draw.house.tool.singledoor',
  DOUBLEDOOR = 'draw.house.tool.doubledoor',
  MOTHERWINDOW = 'draw.house.tool.motherwindow',
  SQUARECOLUMN = 'draw.house.tool.postsquare',
  CYLINDER = 'draw.house.tool.postcircle',
  SIZE = 'draw.house.parameters.size',
  SECTIONROTATION = 'draw.house.parameters.sectionrotation',
  SPACE = 'draw.house.parameters.space',
  DRAWCOLUMN = 'draw.house.parameters.drawpost',
  MODEL_OFFGROUND = 'draw.house.parameters.groundheight',
  SEPARATEWALL = 'draw.house.tool.partition',
  BORDERCLASS_STRAIGHT = 'draw.house.tool.borderclass_straight',
  BORDERCLASS_ARC = 'draw.house.tool.borderclass_arc',
  FLOOR = 'draw.house.parameters.materialFlood',
  EMPTY_LABEL = '',
  FACE = 'draw.house.parameters.materialWall',
  FURNITURE_MULTI = 'draw.house.parameters.furniture_multi',
  WINDOW_MULTI = 'draw.house.parameters.window_multi',
  FURNITURE_SETS = 'draw.house.parameters.furniture_sets'
}

export enum EParamUnit {
  MM = 'mm',
  AREA = 'm²',
  ANGLE = '°'
}

type PrimitiveType = string | number | boolean | object;

interface IEditParamsValue<T = PrimitiveType> {
  label: EParamLabel;
  type: EParamType;
  unit?: EParamUnit;
  options?: {
    label: string;
    value: T;
    /**
     * @description 是否隐藏这个选项
     */
    hide?: boolean;
  }[];
  settings: {
    max?: number;
    min?: number;
    maxLength?: number;
    /**
     * 是否只读
     * @default false
     * 如果为true，则渲染为只读标签
     * 如果为false，则该参数可以编辑
     * 注意：如果 `readonly` 和 `disabled` 同时为 `true`，则 `readonly` 优先级更高
     */
    readonly?: boolean;
    /**
     * 是否禁用
     * @default false
     * 如果为true，则渲染为禁用状态 Input
     * 注意：如果 `readonly` 和 `disabled` 同时为 `true`，则 `readonly` 优先级更高
     * 如果为false，则该参数可以编辑
     */
    disabled?: boolean;
    /**
     * 是否隐藏标签
     * @default false
     * 如果为true，则标签会被隐藏
     *
     * 用来渲染宽度大的输入组件，例如滑块输入组件
     */
    hideLabel?: boolean;
    /**
     * 默认值，仅当类型为input且input值为空时使用，作保底值
     */
    defaultValue?: string;
    /**
     * 当类型为input时是否为"多种数值"
     * @default false
     * 如果为true,则input框内会展示"多种数值"
     */
    isMulti?: boolean;
  };
}

export interface IEditParamsGroup<K> {
  /**
   * 分组的 ID
   *
   * 目前与 `label` 相同
   */
  id: EParamGroupLabel;
  /**
   * 分组的标签
   */
  label: EParamGroupLabel;
  /**
   * 包含在这个组的字段
   *
   * 没有出现在组中的字段不会被渲染
   */
  fields: K[];
  /**
   * 相互关联更新的字段
   *
   * 关联的且在 `fields` 中相邻的属性会渲染出联动按钮，如果关联的属性不相邻属于无效配置
   * @default undefined
   */
  linkedFields?: K[];
  /**
   * 默认关联更新状态
   *
   * @default false,
   * 如果为 true，则默认关联更新
   * 如果为 false，则默认不关联更新
   */
  defaultLinked?: boolean;
  /**
   * 是否出现「恢复默认」按钮
   * @default false
   */
  resettable?: boolean;
  /**
   * 是否隐藏标签
   * @default false
   */
  hideLabel?: boolean;
}

export interface IEditParams {
  id: string;
  type: EEntityType | EOperateMode;
  title: EParamGroupLabel;
  group: IEditParamsGroup<keyof IEditParams['values']>[];
  values: Partial<Record<EParamsKeys, PrimitiveType>>;
  fields: Partial<Record<keyof IEditParams['values'], IEditParamsValue<ValueOf<IEditParams['values']>>>>;
  options?: {
    hideTitle?: boolean;
  };
}

export type IEditParamsOnChange = (params: {
  key: keyof IEditParams['values'];
  value: unknown; // ValueOf<IEditParams['values']>
  values: IEditParams['values'];
  linked?: (keyof IEditParams['values'])[];
  id: IEditParams['id'];
  type: IEditParams['type'];
}) => void;

export type IEditParamsOnReset = (params: Pick<IEditParams, 'id' | 'type'>) => void;
